import { emptyCounts } from './service-activity-matrix';
import {
  ActivityMatrixGroup,
  ActivityMatrixModel,
  ActivityMatrixRow,
  ActivitySort,
  MatrixFocusCell,
} from '../@types/service-activity.type';
import { ACTIVITY_STATUS_COLUMNS } from '../constants/service-activity.constant';

/** Recompute derived aggregates over a subset of groups, so heat and totals
 *  describe what is actually on screen rather than the unfiltered payload. */
const remodel = (groups: ActivityMatrixGroup[]): ActivityMatrixModel => {
  const columnMaxima = emptyCounts();
  const totals = emptyCounts();
  let grandTotal = 0;
  let rowCount = 0;

  for (const group of groups) {
    for (const row of group.rows) {
      rowCount += 1;
      grandTotal += row.total;
      for (const column of ACTIVITY_STATUS_COLUMNS) {
        const value = row.counts[column.field];
        totals[column.field] += value;
        if (value > columnMaxima[column.field]) columnMaxima[column.field] = value;
      }
    }
  }

  return { groups, columnMaxima, totals, grandTotal, rowCount };
};

const matchesQuery = (row: ActivityMatrixRow, needle: string): boolean =>
  row.label.toLowerCase().includes(needle) ||
  row.rowHeading.toLowerCase().includes(needle) ||
  row.clientCode.toLowerCase().includes(needle) ||
  row.serviceTypeName.toLowerCase().includes(needle);

/**
 * Filter leaf rows by a free-text query.
 *
 * A group survives only while at least one of its rows matches; a group whose
 * label matches keeps all its rows, so searching `FSG` shows the whole client.
 * Subtotals and column maxima are recomputed from the surviving rows.
 */
export const filterActivityMatrix = (
  model: ActivityMatrixModel,
  query: string
): ActivityMatrixModel => {
  const needle = query.trim().toLowerCase();
  if (!needle) return model;

  const groups: ActivityMatrixGroup[] = [];

  for (const group of model.groups) {
    const groupMatches = group.label.toLowerCase().includes(needle);
    const rows = groupMatches ? group.rows : group.rows.filter((row) => matchesQuery(row, needle));
    if (rows.length === 0) continue;

    if (rows.length === group.rows.length) {
      groups.push(group);
      continue;
    }

    const subtotals = emptyCounts();
    let total = 0;
    for (const row of rows) {
      total += row.total;
      for (const column of ACTIVITY_STATUS_COLUMNS)
        subtotals[column.field] += row.counts[column.field];
    }
    groups.push({ ...group, rows, subtotals, total });
  }

  return remodel(groups);
};

const rowSortValue = (row: ActivityMatrixRow, sort: ActivitySort): number | string => {
  if (sort.key === 'label') return row.label.toLowerCase();
  if (sort.key === 'total') return row.total;
  return row.counts[sort.key];
};

const groupSortValue = (group: ActivityMatrixGroup, sort: ActivitySort): number | string => {
  if (sort.key === 'label') return group.label.toLowerCase();
  if (sort.key === 'total') return group.total;
  return group.subtotals[sort.key];
};

const compare = (a: number | string, b: number | string, direction: 'asc' | 'desc'): number => {
  const result =
    typeof a === 'string' && typeof b === 'string' ? a.localeCompare(b) : Number(a) - Number(b);
  return direction === 'asc' ? result : -result;
};

/**
 * Sort groups by their subtotal and rows within each group by the same key.
 * Grouping is always preserved — sorting reorders, it never flattens.
 * `Array.prototype.sort` is stable in ES2019+, so equal values keep source order.
 */
export const sortActivityMatrix = (
  model: ActivityMatrixModel,
  sort: ActivitySort | null
): ActivityMatrixModel => {
  if (!sort) return model;

  const groups = model.groups.map((group) => ({
    ...group,
    rows: [...group.rows].sort((a, b) =>
      compare(rowSortValue(a, sort), rowSortValue(b, sort), sort.direction)
    ),
  }));
  groups.sort((a, b) => compare(groupSortValue(a, sort), groupSortValue(b, sort), sort.direction));

  return { ...model, groups };
};

/** Next sort state for a header click: none → desc → asc → none. Counts open
 *  descending because "who has the most" is the question being asked. */
export const nextSortState = (
  current: ActivitySort | null,
  key: ActivitySort['key']
): ActivitySort | null => {
  if (current?.key !== key) return { key, direction: key === 'label' ? 'asc' : 'desc' };
  if (current.direction === 'desc') return { key, direction: 'asc' };
  return null;
};

/**
 * Resolve an arrow-key move across the flattened grid of focusable buttons.
 *
 * Non-interactive cells (zero counts) are skipped — drilling into an empty
 * list is a dead end. Movement clamps at the edges rather than wrapping.
 */
export const getNextMatrixFocus = (
  cells: readonly MatrixFocusCell[],
  current: MatrixFocusCell,
  key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End'
): MatrixFocusCell => {
  const interactive = cells.filter((cell) => cell.interactive);
  if (interactive.length === 0) return current;

  const rowKeys = [...new Set(interactive.map((cell) => cell.rowKey))];
  const rowIndex = rowKeys.indexOf(current.rowKey);
  if (rowIndex === -1) return interactive[0];

  const inRow = (rk: string): MatrixFocusCell[] =>
    interactive.filter((cell) => cell.rowKey === rk).sort((a, b) => a.columnIndex - b.columnIndex);

  if (key === 'Home' || key === 'End') {
    const row = inRow(current.rowKey);
    return key === 'Home' ? row[0] : row[row.length - 1];
  }

  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const row = inRow(current.rowKey);
    const position = row.findIndex((cell) => cell.columnIndex === current.columnIndex);
    const next = key === 'ArrowLeft' ? position - 1 : position + 1;
    return row[next] ?? current;
  }

  // Vertical: land on the nearest interactive column at or before the current
  // one, so travelling down a sparse column doesn't strand focus.
  const targetRow = rowKeys[key === 'ArrowUp' ? rowIndex - 1 : rowIndex + 1];
  if (targetRow === undefined) return current;

  const row = inRow(targetRow);
  const exact = row.find((cell) => cell.columnIndex === current.columnIndex);
  if (exact) return exact;

  const before = row.filter((cell) => cell.columnIndex <= current.columnIndex);
  return before.length > 0 ? before[before.length - 1] : row[0];
};
